import { getRelativeTime } from '@thoughtspot/date-format-util';
import { translate as t } from '@thoughtspot/i18n';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getTSAdminUserGuid, getUserGuid, isMfaEnabled, isPrincipalImportExportEnabled, isScriptabilityEnabled, resetPasswordV2Enabled, } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { getTranslatedInfoForMessageCode } from "../../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../../utils/error-handling/error-utils";
import { MoreOptions } from '../../../user-management/more-options/more-options';
import { deepOmit, getErrorCode } from '../../../user-management/util';
import { EllipsisText } from '../shared';
import styles from './components/users.module.scss';
import { getStatusDisplayLabel, isUserStatusSupported, ListActionTypeV2, TableColumnId, UserAccountStatus, V2TranslationKeys, } from './constants';
const areStateActionsSupported = () => isUserStatusSupported();
const getStatusSpecificActions = (user, dispatch) => {
    switch (user.userState) {
        case UserAccountStatus.PENDING:
            return [
                {
                    id: ListActionTypeV2.RESEND_ACTIVATION_MAIL,
                    label: t('oktaUser.RESEND_ACTIVATION_MAIL'),
                    onClick: dispatch(ListActionTypeV2.RESEND_ACTIVATION_MAIL),
                },
            ];
        case UserAccountStatus.LOCKED:
            return [
                {
                    id: ListActionTypeV2.UNLOCK,
                    label: t('oktaUser.UNLOCK'),
                    onClick: dispatch(ListActionTypeV2.UNLOCK),
                },
            ];
        case UserAccountStatus.SUSPENDED:
            return [
                {
                    id: ListActionTypeV2.UNSUSPEND,
                    label: t('users.v2.okta.unsuspendUser'),
                    onClick: dispatch(ListActionTypeV2.UNSUSPEND),
                },
            ];
        default:
            return [];
    }
};
export const getRowMenuItems = (user, context) => {
    const dispatch = (action) => () => context.onRowAction({ action, data: user });
    const isSelf = user.id === getUserGuid();
    const isTsAdmin = user.id === getTSAdminUserGuid();
    const cannotDelete = isSelf || isTsAdmin;
    const cannotEdit = isTsAdmin && getUserGuid() !== getTSAdminUserGuid();
    const editItem = {
        id: ListActionTypeV2.EDIT,
        label: t(V2TranslationKeys.EDIT),
        onClick: dispatch(ListActionTypeV2.EDIT),
        itemDisabled: cannotEdit,
    };
    const resetPwdItem = {
        id: ListActionTypeV2.RESET_PASSWORD,
        label: t(V2TranslationKeys.RESET_PASSWORD),
        onClick: dispatch(ListActionTypeV2.RESET_PASSWORD),
    };
    const resetMfaItem = {
        id: ListActionTypeV2.RESET_MFA,
        label: t(V2TranslationKeys.RESET_MFA),
        onClick: dispatch(ListActionTypeV2.RESET_MFA),
    };
    const deleteItem = {
        id: ListActionTypeV2.DELETE,
        label: t(V2TranslationKeys.DELETE),
        onClick: dispatch(ListActionTypeV2.DELETE),
        itemDisabled: cannotDelete,
    };
    const exportItem = {
        id: ListActionTypeV2.EXPORT,
        label: t(V2TranslationKeys.EXPORT),
        onClick: dispatch(ListActionTypeV2.EXPORT),
    };
    const statusActions = areStateActionsSupported()
        ? getStatusSpecificActions(user, dispatch)
        : [];
    const primaryGroup = {
        title: 'Primary',
        items: [...statusActions, editItem],
    };
    const showResetMfa = isMfaEnabled() && isOktaEnabled();
    const secondaryGroup = {
        title: 'Secondary',
        items: [
            ...(resetPasswordV2Enabled() ? [resetPwdItem] : []),
            ...(showResetMfa ? [resetMfaItem] : []),
            deleteItem,
        ],
    };
    const exportGroup = {
        title: 'Export',
        items: [exportItem],
    };
    const isTmlEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const groups = [primaryGroup, secondaryGroup];
    if (isTmlEnabled) {
        groups.push(exportGroup);
    }
    return groups;
};
const getStateActionIds = (state) => {
    switch (state) {
        case UserAccountStatus.PENDING:
            return new Set([ListActionTypeV2.RESEND_ACTIVATION_MAIL]);
        case UserAccountStatus.LOCKED:
            return new Set([ListActionTypeV2.UNLOCK]);
        case UserAccountStatus.SUSPENDED:
            return new Set([ListActionTypeV2.UNSUSPEND]);
        default:
            return new Set();
    }
};
export const getBulkActions = (selectedUsers, handlers, isClusterAdmin) => {
    const isTmlEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const exportAction = {
        id: 'export',
        label: t(V2TranslationKeys.EXPORT),
        onClick: handlers.onExport,
    };
    const cannotDeleteSelection = selectedUsers.some(user => user.id === getUserGuid() || user.id === getTSAdminUserGuid());
    const deleteAction = {
        id: 'delete',
        label: t(V2TranslationKeys.DELETE),
        onClick: handlers.onDelete,
        disabled: cannotDeleteSelection,
    };
    const addAction = isClusterAdmin
        ? {
            id: 'addToOrg',
            label: t(V2TranslationKeys.ADD_TO_ORG),
            onClick: handlers.onAddToOrg,
        }
        : {
            id: 'addToGroups',
            label: t(V2TranslationKeys.ADD_TO_GROUPS),
            onClick: handlers.onAddToGroups,
        };
    const tailActions = [
        addAction,
        ...(isTmlEnabled ? [exportAction] : []),
        deleteAction,
    ];
    const stateActionSets = areStateActionsSupported()
        ? selectedUsers.map(u => getStateActionIds(u.userState))
        : [];
    const intersection = stateActionSets.length
        ? stateActionSets.reduce((acc, set) => new Set([...acc].filter(a => set.has(a))))
        : new Set();
    const stateActions = [];
    if (intersection.has(ListActionTypeV2.RESEND_ACTIVATION_MAIL)) {
        stateActions.push({
            id: 'resendActivation',
            label: t('oktaUser.RESEND_ACTIVATION_MAIL'),
            onClick: handlers.onResendActivationAll,
        });
    }
    if (intersection.has(ListActionTypeV2.UNLOCK)) {
        stateActions.push({
            id: 'unlock',
            label: t('oktaUser.UNLOCK'),
            onClick: handlers.onUnlockAll,
        });
    }
    if (intersection.has(ListActionTypeV2.UNSUSPEND)) {
        stateActions.push({
            id: 'unsuspend',
            label: t('users.v2.okta.unsuspendUser'),
            onClick: handlers.onUnsuspendAll,
        });
    }
    return [...stateActions, ...tailActions];
};
const NameCell = ({ data, context }) => {
    const displayName = data.displayName || '';
    const userName = data.name || '';
    return (React.createElement(Vertical, { className: styles.usersNameCell },
        React.createElement(EllipsisText, { variant: "p", color: Colors.accent, className: styles.usersNameLink, onClick: () => context.onRowAction({
                action: ListActionTypeV2.VIEW_DETAILS,
                data,
            }) }, displayName),
        React.createElement(EllipsisText, { variant: "footnote", color: Colors.grayLight }, userName)));
};
export const NamesListCell = ({ names }) => {
    const cellRef = useRef(null);
    const popoverRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(names.length);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    useLayoutEffect(() => {
        const el = cellRef.current;
        if (!el || names.length <= 1)
            return undefined;
        const measure = () => {
            const style = getComputedStyle(el);
            const padding = (parseFloat(style.paddingLeft) || 0) +
                (parseFloat(style.paddingRight) || 0);
            const cellWidth = el.clientWidth - padding;
            if (cellWidth <= 0)
                return;
            const ctx = document.createElement('canvas').getContext('2d');
            if (!ctx)
                return;
            ctx.font = style.font;
            let count = names.length;
            while (count > 1) {
                if (ctx.measureText(names.slice(0, count).join(' , ')).width <=
                    cellWidth)
                    break;
                count--;
            }
            setVisibleCount(count);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [names]);
    if (names.length === 0) {
        return (React.createElement(Typography, { variant: "p", color: Colors.grayLight, className: styles.usersCellContent }, "-"));
    }
    const remaining = names.length - visibleCount;
    return (React.createElement("div", { ref: cellRef, className: styles.namesListCell },
        React.createElement(Typography, { variant: "p", ellipsis: { rows: 1 } }, names.slice(0, visibleCount).join(', ')),
        remaining > 0 && (React.createElement(React.Fragment, null,
            React.createElement("span", { ref: popoverRef, className: styles.moreLink, onClick: () => setIsPopoverOpen(!isPopoverOpen), onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsPopoverOpen(!isPopoverOpen);
                    }
                }, role: "button", tabIndex: 0 }, t(V2TranslationKeys.N_MORE, { count: remaining })),
            React.createElement(Popover, { align: "bottom-start", targetRef: popoverRef, isVisible: isPopoverOpen, onClickOutside: () => setIsPopoverOpen(false), content: React.createElement(Vertical, { className: styles.popoverContent }, names.slice(visibleCount).map(name => (React.createElement(Typography, { key: name, variant: "body-normal", noMargin: true, className: styles.popoverItem }, name)))) })))));
};
const OrgsCell = ({ data, context }) => {
    const orgIds = data.orgIds ?? [];
    const orgNameMap = context.orgNameMap ?? {};
    const names = orgIds.map(id => orgNameMap[id]).filter(Boolean);
    return React.createElement(NamesListCell, { names: names });
};
const GroupsCell = ({ data }) => (React.createElement(NamesListCell, { names: data.groupNames ?? [] }));
const CreatedCell = ({ data }) => (React.createElement(Typography, { variant: "p", className: styles.usersCellContent }, getRelativeTime(data.created)));
const StatusCell = ({ data }) => {
    const status = data.userState;
    const label = getStatusDisplayLabel(status);
    return (React.createElement(Typography, { variant: "p", color: Colors.base, className: styles.usersCellContent }, label));
};
const ActionsCell = ({ data, context }) => {
    const menuItemGroups = getRowMenuItems(data, context);
    return (React.createElement(MoreOptions, { menuId: 1, menuLabel: t(V2TranslationKeys.COL_MORE_OPTIONS), menuItemGroups: menuItemGroups, isDisabled: data.isChecked }));
};
export const getColumnDefinitionsV2 = (isClusterAdmin) => [
    {
        cellRendererFramework: NameCell,
        headerName: t(V2TranslationKeys.COL_NAME),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.NAME,
        suppressMovable: true,
        sortable: true,
        initialSort: 'asc',
        flex: 2,
    },
    {
        cellRendererFramework: isClusterAdmin ? OrgsCell : GroupsCell,
        headerName: isClusterAdmin
            ? t(V2TranslationKeys.COL_ORGS)
            : t(V2TranslationKeys.COL_GROUPS),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.GROUPS_OR_ORGS,
        suppressMovable: true,
        sortable: false,
        flex: 2,
    },
    {
        cellRendererFramework: CreatedCell,
        headerName: t(V2TranslationKeys.COL_CREATED),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.CREATED,
        suppressMovable: true,
        sortable: true,
        flex: 1,
    },
    ...(areStateActionsSupported()
        ? [
            {
                cellRendererFramework: StatusCell,
                headerName: t(V2TranslationKeys.COL_STATUS),
                headerClass: [styles.headerStyle],
                colId: TableColumnId.STATUS,
                suppressMovable: true,
                sortable: false,
                flex: 1,
            },
        ]
        : []),
    {
        cellRendererFramework: ActionsCell,
        colId: TableColumnId.ACTIONS,
        headerName: t(V2TranslationKeys.COL_ACTIONS),
        headerClass: [styles.headerStyle],
        cellClass: [styles.usersActionsCell],
        sortable: false,
        width: 78,
        suppressMovable: true,
    },
];
export const cleanUserPayload = (obj) => {
    const cleaned = deepOmit(obj, ['__typename', 'displayNameLastUpdatedBy']);
    if (!cleaned.userContent?.userPreferences?.preferredLocale) {
        delete cleaned.userContent?.userPreferences?.preferredLocale;
    }
    if (!cleaned.userContent?.userProperties?.persona) {
        delete cleaned.userContent?.userProperties?.persona;
    }
    return cleaned;
};
export const handleGraphQLError = (err, onError, translate) => {
    const graphQlError = getErrorObjectFromGraphQLErrors(err);
    const errorCode = getErrorCode(graphQlError);
    const info = getTranslatedInfoForMessageCode(errorCode);
    if (info.summary)
        onError(translate(info.summary));
    return { graphQlError, errorCode, info };
};
//# sourceMappingURL=utils.js.map